import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Shield,
    Users,
    FolderKanban,
    Lock,
    ArrowRight,
    CheckCircle,
    Sparkles,
    BarChart3
} from "lucide-react";
import teams from "../../imgs/team-colab.svg";

const FrontPage = () => {
    const navigate = useNavigate();

    const features = [
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Enterprise Security",
            description: "Role-based access control with granular permissions and audit trails"
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Team Collaboration",
            description: "Seamless communication and workflow coordination across teams"
        },
        {
            icon: <FolderKanban className="w-6 h-6" />,
            title: "Project Management",
            description: "Comprehensive tools for project tracking and resource allocation"
        },
        {
            icon: <Lock className="w-6 h-6" />,
            title: "Data Protection",
            description: "End-to-end encryption and compliance with industry standards"
        },
        {
            icon: <BarChart3 className="w-6 h-6" />,
            title: "Analytics Dashboard",
            description: "Real-time insights and performance metrics for decision making"
        },
        {
            icon: <Sparkles className="w-6 h-6" />,
            title: "Smart Automation",
            description: "AI-powered workflows and intelligent task assignment"
        }
    ];

    const testimonials = [
        {
            text: "TeamHub transformed our workflow efficiency by 40%",
            author: "Sarah Chen, CTO at TechCorp"
        },
        {
            text: "The RBAC system is the most intuitive we've ever used",
            author: "Michael Rodriguez, Lead Developer"
        },
        {
            text: "Implementation was seamless, support team was exceptional",
            author: "Elena Petrova, Project Manager"
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-primary-50/30 to-background">
            {/* Navigation */}
            <nav className="container mx-auto px-6 py-4">
                <div className="flex items-center justify-between">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center space-x-2"
                    >
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl flex items-center justify-center">
                            <Shield className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              TeamHub
            </span>
                    </motion.div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate("/login")}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                            Sign In
                        </button>
                        <button
                            onClick={() => navigate("/signup")}
                            className="btn-primary"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="container mx-auto px-6 py-12 md:py-20"
            >
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
                                <Sparkles className="w-4 h-4" />
                                <span className="text-sm font-medium">Enterprise Ready</span>
                            </div>

                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                                Secure Collaboration for{" "}
                                <span className="gradient-text">Modern Teams</span>
                            </h1>

                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                TeamHub provides a comprehensive role-based access control system
                                designed for enterprises that demand security without compromising
                                productivity. Manage permissions, streamline workflows, and foster
                                collaboration across your organization.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    className="btn-primary group"
                                    onClick={() => navigate("/signup")}
                                >
                                    Start Free Trial
                                    <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    className="btn-outline"
                                    onClick={() => window.open("#demo", "_self")}
                                >
                                    View Demo
                                </button>
                            </div>

                            <div className="mt-8 flex items-center space-x-6">
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span className="text-sm">SOC 2 Compliant</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span className="text-sm">99.9% Uptime</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span className="text-sm">24/7 Support</span>
                                </div>
                            </div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-strong">
                                <img
                                    src={teams}
                                    alt="Team Collaboration"
                                    className="w-full h-auto"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-primary-600/10 to-transparent" />
                            </div>

                            {/* Floating Elements */}
                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="absolute -top-4 -left-4 bg-white p-3 rounded-xl shadow-medium"
                            >
                                <Users className="w-6 h-6 text-primary-600" />
                            </motion.div>

                            <motion.div
                                animate={{ y: [0, -15, 0] }}
                                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
                                className="absolute -bottom-4 -right-4 bg-secondary-600 p-3 rounded-xl shadow-medium"
                            >
                                <Lock className="w-6 h-6 text-white" />
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Features Grid */}
            <section className="container mx-auto px-6 py-16">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Everything You Need for Secure Collaboration
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Built with enterprise security standards and user-friendly design principles
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="card group hover:border-primary-300"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="bg-gradient-to-r from-primary-50/50 to-secondary-50/50 py-16">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-12">
                            Trusted by Industry Leaders
                        </h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {testimonials.map((testimonial, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="bg-white p-6 rounded-xl shadow-soft"
                                >
                                    <div className="text-primary-600 mb-4">★★★★★</div>
                                    <p className="text-gray-700 italic mb-4">"{testimonial.text}"</p>
                                    <p className="text-sm text-gray-600">{testimonial.author}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="container mx-auto px-6 py-20">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 p-8 md:p-12"
                    >
                        <div className="relative z-10">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Ready to Transform Your Team's Collaboration?
                            </h2>
                            <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
                                Join thousands of organizations that trust TeamHub for secure,
                                efficient, and scalable team management.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                                    onClick={() => navigate("/signup")}
                                >
                                    Start Free Trial
                                </button>
                                <button
                                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-all duration-200"
                                    onClick={() => navigate("/login")}
                                >
                                    Schedule a Demo
                                </button>
                            </div>
                            <p className="text-primary-200 text-sm mt-6">
                                No credit card required • 14-day free trial • Cancel anytime
                            </p>
                        </div>

                        {/* Background Pattern */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-32 -translate-x-32" />
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-primary-600" />
                                </div>
                                <span className="text-xl font-bold">TeamHub</span>
                            </div>
                            <p className="text-gray-400 text-sm">
                                Secure collaboration platform for modern enterprises
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-4">Product</h3>
                            <ul className="space-y-2 text-gray-400 text-sm">
                                <li><a href="#" className="hover:text-white transition">Features</a></li>
                                <li><a href="#" className="hover:text-white transition">Pricing</a></li>
                                <li><a href="#" className="hover:text-white transition">API</a></li>
                                <li><a href="#" className="hover:text-white transition">Documentation</a></li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-4">Company</h3>
                            <ul className="space-y-2 text-gray-400 text-sm">
                                <li><a href="#" className="hover:text-white transition">About</a></li>
                                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                                <li><a href="#" className="hover:text-white transition">Careers</a></li>
                                <li><a href="#" className="hover:text-white transition">Contact</a></li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-4">Legal</h3>
                            <ul className="space-y-2 text-gray-400 text-sm">
                                <li><a href="#" className="hover:text-white transition">Privacy Policy</a></li>
                                <li><a href="#" className="hover:text-white transition">Terms of Service</a></li>
                                <li><a href="#" className="hover:text-white transition">Cookie Policy</a></li>
                                <li><a href="#" className="hover:text-white transition">GDPR</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
                        <p>&copy; {new Date().getFullYear()} TeamHub. All rights reserved.</p>
                        <p className="mt-2">Secure • Reliable • Scalable</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default FrontPage;